import { forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CloseCircleIcon from '@/public/icons/approval/close-circle.svg';
import PlusIcon from '@/public/icons/approval/plus.svg';
import {
  RoundedIconButton,
  SearchAddButton,
  SearchBarRoot,
  SearchInputActions,
  SearchInputField,
} from './styles';

type SearchBarProps = {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  ({ placeholder, value, onChange, onClear, onKeyDown }, ref) => {
    const { t } = useTranslation();
    // 컨테이너 클릭 시 input focus 용 — 외부에서 ref가 안 들어와도 동작하도록 자체 fallback ref.
    const fallbackRef = useRef<HTMLInputElement | null>(null);
    const setRefs = (node: HTMLInputElement | null) => {
      fallbackRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };
    const hasKeyword = value.length > 0;

    return (
      <SearchBarRoot onClick={() => fallbackRef.current?.focus()}>
        <SearchInputField
          ref={setRefs}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <SearchInputActions>
          {hasKeyword ? (
            <RoundedIconButton
              type="button"
              aria-label={t('검색어 지우기')}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
                fallbackRef.current?.focus();
              }}
            >
              <CloseCircleIcon width={18} height={18} />
            </RoundedIconButton>
          ) : (
            <SearchAddButton type="button" aria-label={t('추가')} tabIndex={-1}>
              <PlusIcon
                width={14}
                height={14}
                style={{ ['--icon-stroke-width' as any]: 0.9 }}
              />
            </SearchAddButton>
          )}
        </SearchInputActions>
      </SearchBarRoot>
    );
  },
);

SearchBar.displayName = 'SearchBar';

export default SearchBar;
